import { duration, MagicConfigMixin } from '@gig/magic-common';
import { __extends, __decorate, __metadata } from 'tslib';
import { Prop, Watch, Emit, Component, Mixins } from 'vue-property-decorator';

var TimeSegment;
(function (TimeSegment) {
    TimeSegment["DAYS"] = "days";
    TimeSegment["HOURS"] = "hours";
    TimeSegment["MINUTES"] = "minutes";
    TimeSegment["SECONDS"] = "seconds";
})(TimeSegment || (TimeSegment = {}));
/**
 * The countdown component simply counts down to a specified
 * date in the future.
 */
var CountdownComponent = /** @class */ (function (_super) {
    __extends(CountdownComponent, _super);
    function CountdownComponent() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.intervalId = null;
        // Value to keep track of if we have emitted an event for the specified time
        // countdown reaching 0 already.
        _this.emittedTime = null;
        _this.durationRemaining = {};
        return _this;
    }
    Object.defineProperty(CountdownComponent.prototype, "digits", {
        get: function () {
            var _this = this;
            return this.activeSegments.map(function (segment) { return _this.durationRemaining[segment].value; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(CountdownComponent.prototype, "labels", {
        get: function () {
            var _this = this;
            return this.activeSegments.map(function (segment) { return _this.durationRemaining[segment].name; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(CountdownComponent.prototype, "asString", {
        get: function () {
            var _this = this;
            var hasDays = this.durationRemaining[TimeSegment.DAYS].isMeaningful;
            var hasHours = this.durationRemaining[TimeSegment.HOURS].isMeaningful &&
                this.durationRemaining[TimeSegment.HOURS].value !== '00';
            var delimiter = this.colonTimeFormat && !hasDays ? ':' : ' ';
            var formattedSegments = this.activeSegments
                .filter(function (segment) {
                if (_this.meaningfulOnly &&
                    !_this.durationRemaining[segment].isMeaningful) {
                    return false;
                }
                if (_this.colonTimeFormat && hasDays) {
                    return (segment !== TimeSegment.MINUTES &&
                        segment !== TimeSegment.SECONDS &&
                        !(segment === TimeSegment.HOURS && !hasHours));
                }
                return true;
            })
                .map(function (segment) {
                var value = _this.durationRemaining[segment].value;
                return _this.colonTimeFormat && !hasDays
                    ? value
                    : "" + value + _this.$t("countdown.simple.label." + segment);
            })
                .filter(Boolean)
                .join(delimiter);
            return this.monospace
                ? formattedSegments.replace(/\w|\s/g, '<span class="character">$&</span>')
                : formattedSegments;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(CountdownComponent.prototype, "activeSegments", {
        get: function () {
            return this.segments
                .split(',')
                .map(function (segment) { return segment.trim(); });
        },
        enumerable: false,
        configurable: true
    });
    CountdownComponent.prototype.created = function () {
        var _this = this;
        // 500ms as otherwise it's likely the countdown will skip every now and then.
        this.intervalId = setInterval(function () {
            _this.updateTime();
        }, 500);
    };
    CountdownComponent.prototype.destroyed = function () {
        clearInterval(this.intervalId);
    };
    CountdownComponent.prototype.updateTime = function () {
        var _a, _b;
        if (this.to) {
            var timeLeft = this.to - Date.now() || 0;
            var _c = duration(timeLeft), days = _c.days, hours = _c.hours, minutes = _c.minutes, seconds = _c.seconds;
            this.durationRemaining = (_a = {},
                _a[TimeSegment.DAYS] = {
                    name: 'days',
                    value: this.padValue(Math.max(0, days)),
                    isMeaningful: days > 0,
                },
                _a[TimeSegment.HOURS] = {
                    name: 'hours',
                    value: this.padValue(Math.max(0, hours)),
                    isMeaningful: days > 0 || hours > 0,
                },
                _a[TimeSegment.MINUTES] = {
                    name: 'minutes',
                    value: this.padValue(Math.max(0, minutes)),
                    isMeaningful: days > 0 || hours > 0 || minutes > 0,
                },
                _a[TimeSegment.SECONDS] = {
                    name: 'seconds',
                    value: this.padValue(Math.max(0, seconds)),
                    isMeaningful: true,
                },
                _a);
            if (timeLeft <= 0 && this.emittedTime !== this.to) {
                this.emittedTime = this.to;
                this.ended();
            }
        }
        else {
            this.durationRemaining = (_b = {},
                _b[TimeSegment.DAYS] = {
                    name: 'days',
                    value: '--',
                    isMeaningful: false,
                },
                _b[TimeSegment.HOURS] = {
                    name: 'hours',
                    value: '--',
                    isMeaningful: false,
                },
                _b[TimeSegment.MINUTES] = {
                    name: 'minutes',
                    value: '--',
                    isMeaningful: false,
                },
                _b[TimeSegment.SECONDS] = {
                    name: 'seconds',
                    value: '--',
                    isMeaningful: false,
                },
                _b);
        }
    };
    CountdownComponent.prototype.padValue = function (value) {
        return value.toString().padStart(2, '0');
    };
    /**
     * Emitted once the countdown reaches 0.
     */
    CountdownComponent.prototype.ended = function () {
        return;
    };
    __decorate([
        Prop(Number),
        __metadata("design:type", Object)
    ], CountdownComponent.prototype, "to", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], CountdownComponent.prototype, "simple", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], CountdownComponent.prototype, "colonTimeFormat", void 0);
    __decorate([
        Prop({
            type: String,
            validator: function (values) {
                return (!!values &&
                    values
                        .split(',')
                        .map(function (value) { return value.trim(); })
                        .every(function (segment) {
                        return Object.values(TimeSegment).includes(segment);
                    }));
            },
            default: function () { return Object.values(TimeSegment).join(','); },
        }),
        __metadata("design:type", String)
    ], CountdownComponent.prototype, "segments", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], CountdownComponent.prototype, "meaningfulOnly", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], CountdownComponent.prototype, "monospace", void 0);
    __decorate([
        Watch('to', { immediate: true }),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], CountdownComponent.prototype, "updateTime", null);
    __decorate([
        Emit('ended'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], CountdownComponent.prototype, "ended", null);
    CountdownComponent = __decorate([
        Component({
            name: 'countdown',
        })
    ], CountdownComponent);
    return CountdownComponent;
}(Mixins(MagicConfigMixin)));

export default CountdownComponent;
export { TimeSegment };
